import React, { useState, useEffect } from 'react';
import { FaCheck, FaTimes, FaPlay, FaPause, FaHeart, FaStar, FaLightbulb } from 'react-icons/fa';
import ChallengeFillBlanksExercise from './ChallengeFillBlanksExercise';
import { getUserChallengeSession } from '../../lib/challenges';
import { getCurrentUser } from '../../lib/auth';

interface Song {
  id: string;
  song_url: string;
  song_cover: string;
  creator: string;
  language: string;
  song_lyrics: string;
}

interface FillBlanksExerciseProps {
  song: Song;
  onComplete: (results: any) => void;
  challengeMode?: boolean;
  sessionId?: string;
  expiresAt?: string;
  rewardPoints?: number;
  onExitChallenge?: () => void;
}

interface BlankQuestion {
  id: number;
  sentence: string;
  blanks: { word: string; position: number }[];
  userAnswers: string[];
}

export default function FillBlanksExercise({ 
  song, 
  onComplete, 
  challengeMode = false,
  sessionId,
  expiresAt,
  rewardPoints,
  onExitChallenge 
}: FillBlanksExerciseProps) {
  const [questions, setQuestions] = useState<BlankQuestion[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userAnswers, setUserAnswers] = useState<string[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [startTime] = useState(Date.now());
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioRef] = useState(new Audio(song.song_url));
  const [streak, setStreak] = useState(0);
  const [userCurrentChallenge, setUserCurrentChallenge] = useState<any>(null);
  const user = getCurrentUser();

  useEffect(() => {
    generateQuestions();
    if (user) {
      checkUserChallenge();
    }
    return () => {
      audioRef.pause();
    };
  }, []);

  const checkUserChallenge = async () => {
    if (!user) return;
    try {
      const currentChallenge = await getUserChallengeSession(user.id);
      setUserCurrentChallenge(currentChallenge);
    } catch (error) {
      console.error('Error checking user challenge:', error);
    }
  };

  // If user is in a challenge and this is the challenge song, show challenge mode
  if (userCurrentChallenge && userCurrentChallenge.songId === song.id && !challengeMode) {
    return (
      <ChallengeFillBlanksExercise
        song={song}
        sessionId={userCurrentChallenge.sessionId}
        expiresAt={userCurrentChallenge.expiresAt}
        rewardPoints={100}
        onComplete={onComplete}
        onExit={onExitChallenge || (() => {})}
      />
    );
  }

  const generateQuestions = () => {
    // Clean and filter Spanish lyrics
    const cleanedLyrics = song.song_lyrics
      .replace(/\[.*?\]/g, '') // Remove [Verse], [Chorus] etc.
      .replace(/\(.*?\)/g, '') // Remove parentheses content
      .replace(/\n+/g, '\n') // Remove multiple line breaks
      .trim();

    const sentences = cleanedLyrics
      .split('\n')
      .map(line => line.trim())
      .filter(line => {
        // Filter for meaningful Spanish sentences
        return line.length > 15 && 
               line.split(' ').length >= 4 && 
               line.split(' ').length <= 15 &&
               !line.match(/^[A-Z\s]+$/) && // Not all caps
               line.match(/[a-záéíóúñü]/i); // Contains Spanish characters
      })
      ; // Use ALL sentences (no limit)

    if (sentences.length === 0) {
      // Fallback: split by periods or other punctuation
      const fallbackSentences = cleanedLyrics
        .split(/[.!?]/)
        .map(s => s.trim())
        .filter(s => s.length > 10 && s.split(' ').length >= 3)
        ; // Use ALL fallback sentences
      
      if (fallbackSentences.length > 0) {
        generateQuestionsFromSentences(fallbackSentences);
      } else {
        // Last resort: use lines as they are
        generateQuestionsFromSentences(song.song_lyrics.split('\n').filter(l => l.trim().length > 5));
      }
    } else {
      generateQuestionsFromSentences(sentences);
    }
  };

  const generateQuestionsFromSentences = (sentences: string[]) => {
    const generatedQuestions: BlankQuestion[] = sentences.map((sentence, index) => {
      const words = sentence.split(' ').filter(word => word.trim().length > 0);
      
      // Select words to blank out (prefer longer, meaningful words)
      const meaningfulWords = words
        .map((word, idx) => ({ word: word.replace(/[.,!?;:"()]/g, ''), index: idx, length: word.length }))
        .filter(item => {
          const cleanWord = item.word.toLowerCase();
          // Skip very short words and common Spanish articles/prepositions
          const skipWords = ['el', 'la', 'los', 'las', 'un', 'una', 'de', 'del', 'en', 'con', 'por', 'para', 'que', 'se', 'te', 'me', 'le', 'lo', 'su', 'mi', 'tu', 'es', 'son', 'y', 'o', 'a'];
          return item.length > 3 && !skipWords.includes(cleanWord);
        })
        .sort((a, b) => b.length - a.length); // Sort by length, longest first

      // Select 2-3 words to blank out
      const numBlanks = Math.min(3, Math.max(1, Math.floor(meaningfulWords.length / 3)));
      const selectedWords = meaningfulWords.slice(0, numBlanks);

      const blanks = selectedWords.map(item => ({
        word: item.word,
        position: item.index
      }));

      // Create sentence with blanks
      let sentenceWithBlanks = sentence;
      const blankPositions = selectedWords.map(w => w.index).sort((a, b) => b - a);
      
      blankPositions.forEach(pos => {
        const wordsArray = sentenceWithBlanks.split(' ');
        if (pos < wordsArray.length) {
          wordsArray[pos] = '_____';
          sentenceWithBlanks = wordsArray.join(' ');
        }
      });

      return {
        id: index,
        sentence: sentenceWithBlanks,
        blanks,
        userAnswers: new Array(blanks.length).fill('')
      };
    });

    setQuestions(generatedQuestions);
    if (generatedQuestions.length > 0) {
      setUserAnswers(new Array(generatedQuestions[0].blanks.length).fill(''));
    }
  };

  const handleAnswerChange = (blankIndex: number, value: string) => {
    const newAnswers = [...userAnswers];
    newAnswers[blankIndex] = value;
    setUserAnswers(newAnswers);
  };

  const handleNextQuestion = () => {
    // Save current answers
    const updatedQuestions = [...questions];
    updatedQuestions[currentQuestion].userAnswers = [...userAnswers];
    setQuestions(updatedQuestions);

    // Check if answer is correct for streak
    const question = questions[currentQuestion];
    let correctCount = 0;
    question.blanks.forEach((blank, index) => {
      const userAnswer = userAnswers[index]?.toLowerCase().trim().replace(/[.,!?;:"()]/g, '');
      const correctAnswer = blank.word.toLowerCase().trim().replace(/[.,!?;:"()]/g, '');
      
      if (userAnswer === correctAnswer || 
          userAnswer.includes(correctAnswer) || 
          correctAnswer.includes(userAnswer)) {
        correctCount++;
      }
    });

    if (correctCount === question.blanks.length) {
      setStreak(prev => prev + 1);
    }

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setUserAnswers(new Array(questions[currentQuestion + 1].blanks.length).fill(''));
    } else {
      calculateResults();
    }
  };

  const calculateResults = () => {
    let totalCorrect = 0;
    let totalQuestions = 0;

    questions.forEach(question => {
      question.blanks.forEach((blank, index) => {
        totalQuestions++;
        const userAnswer = question.userAnswers[index]?.toLowerCase().trim().replace(/[.,!?;:"()]/g, '');
        const correctAnswer = blank.word.toLowerCase().trim().replace(/[.,!?;:"()]/g, '');
        
        // More flexible matching for Spanish words
        if (userAnswer === correctAnswer || 
            userAnswer.includes(correctAnswer) || 
            correctAnswer.includes(userAnswer)) {
          totalCorrect++;
        }
      });
    });

    setScore(totalCorrect);
    setShowResults(true);

    const timeTaken = Math.floor((Date.now() - startTime) / 1000);
    onComplete({
      score: totalCorrect,
      totalQuestions,
      timeTaken,
      answers: questions.map(q => q.userAnswers)
    });
  };

  const toggleAudio = () => {
    if (isPlaying) {
      audioRef.pause();
    } else {
      audioRef.play();
    }
    setIsPlaying(!isPlaying);
  };

  const renderQuestion = () => {
    const question = questions[currentQuestion];
    if (!question) return null;

    const progress = ((currentQuestion + 1) / questions.length) * 100;

    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
        {/* Animated Progress Header */}
        <div className="text-center space-y-3 sm:space-y-4">
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4 mb-3 sm:mb-4">
            <div className="flex items-center space-x-2">
              <FaStar className="text-yellow-400 animate-pulse w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-white font-bold text-base sm:text-lg">Pregunta {currentQuestion + 1}</span>
            </div>
            <div className="flex items-center space-x-2">
              <FaHeart className="text-yellow-400 w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-white font-bold text-sm sm:text-base">Racha: {streak}</span>
            </div>
          </div>
          
          {/* Animated Progress Bar */}
          <div className="relative w-full h-2 sm:h-3 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="absolute left-0 top-0 h-full bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute inset-0 bg-white/30 animate-pulse rounded-full"></div>
            </div>
            <div className="absolute right-1 sm:right-2 top-1/2 transform -translate-y-1/2 text-xs font-bold text-black">
              {Math.round(progress)}%
            </div>
          </div>
        </div>

        {/* Main Question Card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-yellow-400/30 shadow-2xl">
          <div className="text-center mb-6 sm:mb-8">
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-3 sm:mb-4 animate-fade-in">
              Completa las palabras que faltan
            </h3>
            <div className="text-base sm:text-lg lg:text-xl text-white/80 leading-relaxed">
              {question.sentence.split('_____').map((part, index) => (
                <span key={index} className="inline-block">
                  {part}
                  {index < question.blanks.length && (
                    <input
                      type="text"
                      value={userAnswers[index] || ''}
                      onChange={(e) => handleAnswerChange(index, e.target.value)}
                      className="mx-1 sm:mx-2 px-2 sm:px-3 lg:px-4 py-1 sm:py-2 bg-black/30 backdrop-blur-lg border border-yellow-400/50 rounded-lg sm:rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-center font-bold transition-all duration-300 hover:bg-black/40 text-sm sm:text-base lg:text-lg"
                      placeholder="..."
                      style={{ 
                        width: `${Math.max(80, Math.min(150, (question.blanks[index]?.word.length || 5) * 15))}px`,
                        maxWidth: '90vw'
                      }}
                    />
                  )}
                </span>
              ))}
            </div>
          </div>

          {/* Study Tip - General guidance without revealing answers */}
          <div className="text-center mb-4 sm:mb-6">
            <div className="p-3 sm:p-4 bg-blue-400/10 backdrop-blur-lg rounded-xl sm:rounded-2xl border border-blue-400/20">
              <p className="text-blue-200 text-xs sm:text-sm">
                💡 Escucha la canción para ayudarte con las palabras que faltan
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0 sm:space-x-4">
          <button
            onClick={toggleAudio}
            className="w-full sm:w-auto flex items-center justify-center px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black rounded-xl sm:rounded-2xl hover:from-yellow-500 hover:to-yellow-700 transition-all duration-300 transform hover:scale-105 shadow-lg font-bold text-sm sm:text-base"
          >
            {isPlaying ? <FaPause className="mr-2 sm:mr-3 w-3 h-3 sm:w-4 sm:h-4" /> : <FaPlay className="mr-2 sm:mr-3 w-3 h-3 sm:w-4 sm:h-4" />}
            <span>{isPlaying ? 'Pausar' : 'Escuchar'}</span>
          </button>

          <button
            onClick={handleNextQuestion}
            disabled={userAnswers.some(answer => !answer.trim())}
            className="w-full sm:w-auto flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-white to-gray-100 text-black rounded-xl sm:rounded-2xl hover:from-gray-100 hover:to-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 shadow-lg font-bold text-sm sm:text-base"
          >
            {currentQuestion < questions.length - 1 ? 'Siguiente' : 'Terminar'}
            <FaCheck className="ml-2 sm:ml-3 w-3 h-3 sm:w-4 sm:h-4" />
          </button>
        </div>
      </div>
    );
  };

  const renderResults = () => {
    const totalQuestions = questions.reduce((sum, q) => sum + q.blanks.length, 0);
    const percentage = Math.round((score / totalQuestions) * 100);

    return (
      <div className="p-4 sm:p-6 lg:p-8 text-center space-y-6 sm:space-y-8">
        {/* Celebration Header */}
        <div className="space-y-4 sm:space-y-6">
          <div className="text-6xl sm:text-7xl lg:text-8xl animate-bounce">🎉</div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">¡Increíble!</h2>
          <div className="text-6xl sm:text-7xl lg:text-8xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent animate-pulse">
            {percentage}%
          </div>
          <p className="text-white/80 text-lg sm:text-xl px-4">
            Obtuviste {score} de {totalQuestions} respuestas correctas
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4">
            <div className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-yellow-400/20 rounded-full">
              <FaStar className="text-yellow-400 w-4 h-4" />
              <span className="text-white font-bold text-sm sm:text-base">Racha Final: {streak}</span>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-yellow-400/30">
          <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">Resumen de Respuestas</h3>
          <div className="space-y-2 sm:space-y-3 max-h-48 sm:max-h-60 overflow-y-auto">
            {questions.map((question, qIndex) => (
              <div key={qIndex} className="text-left bg-white/5 rounded-lg sm:rounded-xl p-3 sm:p-4">
                <p className="font-medium text-white mb-2 text-sm sm:text-base">Pregunta {qIndex + 1}:</p>
                <div className="space-y-1 sm:space-y-2">
                  {question.blanks.map((blank, bIndex) => {
                    const userAnswer = question.userAnswers[bIndex]?.toLowerCase().trim().replace(/[.,!?;:"()]/g, '');
                    const correctAnswer = blank.word.toLowerCase().trim().replace(/[.,!?;:"()]/g, '');
                    const isCorrect = userAnswer === correctAnswer || 
                                    userAnswer.includes(correctAnswer) || 
                                    correctAnswer.includes(userAnswer);
                    
                    return (
                      <div key={bIndex} className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-1 sm:space-y-0">
                        <span className="text-white/70 text-xs sm:text-sm">Esperado: <span className="font-bold text-white">{blank.word}</span></span>
                        <div className="flex items-center space-x-2">
                          <span className={`font-bold text-xs sm:text-sm ${isCorrect ? 'text-yellow-400' : 'text-red-400'}`}>
                            {question.userAnswers[bIndex] || '(vacío)'}
                          </span>
                          {isCorrect ? (
                            <FaCheck className="text-yellow-400 w-3 h-3 sm:w-4 sm:h-4" />
                          ) : (
                            <FaTimes className="text-red-400 w-3 h-3 sm:w-4 sm:h-4" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={() => window.location.reload()}
          className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black rounded-xl sm:rounded-2xl hover:from-yellow-500 hover:to-yellow-700 transition-all duration-300 transform hover:scale-105 shadow-lg font-bold text-sm sm:text-base"
        >
          Intentar de Nuevo
        </button>
      </div>
    );
  };

  if (questions.length === 0) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-yellow-400 border-t-transparent mx-auto mb-4"></div>
        <p className="text-white text-base sm:text-lg">Generando preguntas mágicas...</p>
      </div>
    );
  }

  return (
    <div className="min-h-[500px] sm:min-h-[600px]">
      {showResults ? renderResults() : renderQuestion()}
      
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
      `}</style>
    </div>
  );
}